import { useCallback, useRef, useState } from "react";
import { FaPlus } from "react-icons/fa";
import { Box } from "../../components/Box";
import { ExpansionPanel } from "../../components/ExpansionPanel";
import { Flex } from "../../components/Flex";
import { H2 } from "../../components/Heading";
import { Loader } from "../../components/Loader";
import { Paragraph } from "../../components/Paragraph";
import { Button } from "../../components/Button";
import { Image } from "../../components/Image";
import { AddEditDivisionModal } from "../../components/Modals/AddEditDivisionModal";
import { AddEditTeamModal } from "../../components/Modals/AddEditTeamModal";
import { ConfirmModal } from "../../components/Modals/ConfirmModal";
import editIcon from "../../assets/settings/edit.svg";
import deleteIcon from "../../assets/settings/delete-red.svg";
import { useDivisions } from "../../data/useDivisions";
import { useTeams } from "../../data/useTeams";
import { useDeleteDivision } from "../../data/useDeleteDivision";
import { useDeleteTeam } from "../../data/useDeleteTeam";
import { useClickOutside } from "../../hooks/useClickOutside";
import { queryClient } from "../../queryClient";
import { showError } from "../../utils/error-handling";
import { notify } from "../../utils/notify";
import { Division } from "../../types/Division";
import { Team } from "../../types/Team";
import { teamDeleteBlockedReason } from "../../utils/teamDeletion";
import { useAuth } from "../../context/AuthContext";
import styled from "styled-components";

const DefaultPill = styled.div`
  ${({ theme: { colors, fontSizes, space } }) => `
    border-radius: 25px;
    width: max-content;
    display: flex;
    align-items: center;
    padding: ${space[0]} ${space[2]};
    font-family: "Inter", sans-serif;
    font-size: ${fontSizes[0]};
    background: ${colors.gray[10]};
    color: ${colors.gray[60]};
  `}
`;

const SettingsDivisionsTeams = () => {
  const { hasPermission } = useAuth();
  const { data: divisions, isLoading: isLoadingDivisions } = useDivisions();
  const { data: teams, isLoading: isLoadingTeams } = useTeams();

  const { mutate: deleteDivision } = useDeleteDivision();
  const { mutate: deleteTeam } = useDeleteTeam();

  const teamDeleteBlocked = (team: Team) =>
    teamDeleteBlockedReason(team, hasPermission("teams.manage"));

  const [isNewMenuOpen, setIsNewMenuOpen] = useState(false);
  const newMenuRef = useRef<HTMLDivElement | null>(null);
  useClickOutside(newMenuRef, isNewMenuOpen, useCallback(() => setIsNewMenuOpen(false), []));

  const [editingDivision, setEditingDivision] = useState<Division | null>(null);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [isDivisionModalOpen, setIsDivisionModalOpen] = useState(false);
  const [isTeamModalOpen, setIsTeamModalOpen] = useState(false);
  const [pendingDeletion, setPendingDeletion] = useState<
    { kind: "division"; item: Division } | { kind: "team"; item: Team } | null
  >(null);

  const isLoading = isLoadingDivisions || isLoadingTeams;

  const teamsByDivision = (divisionId: number) =>
    teams?.data?.filter(({ division }) => division === divisionId) ?? [];

  const openNewDivision = () => {
    setIsNewMenuOpen(false);
    setEditingDivision(null);
    setIsDivisionModalOpen(true);
  };

  const openNewTeam = () => {
    setIsNewMenuOpen(false);
    setEditingTeam(null);
    setIsTeamModalOpen(true);
  };

  const openEditDivision = (division: Division) => {
    setEditingDivision(division);
    setIsDivisionModalOpen(true);
  };

  const openEditTeam = (team: Team) => {
    setEditingTeam(team);
    setIsTeamModalOpen(true);
  };

  const confirmPendingDeletion = () => {
    if (!pendingDeletion) return;

    if (pendingDeletion.kind === "division") {
      deleteDivision(
        { id: pendingDeletion.item.id },
        {
          onSuccess: () => {
            notify("Division deactivated", { type: "success" });
            queryClient.invalidateQueries(["divisions"]);
          },
          onError: (err: any) =>
            showError(
              err,
              "This Division still has Teams or configuration linked to it — remove those first."
            ),
        }
      );
    } else {
      deleteTeam(
        { id: pendingDeletion.item.id },
        {
          onSuccess: () => {
            notify("Team deleted", { type: "success" });
            queryClient.invalidateQueries(["teams"]);
          },
          onError: (err: any) =>
            showError(
              err,
              "This Team still has members or Mandates linked to it — reassign those first."
            ),
        }
      );
    }

    setPendingDeletion(null);
  };

  return (
    <Box
      bg="white"
      borderRadius={0}
      p={3}
      mb={3}
      maxHeight="calc(100vh - 240px)"
      overflowY="auto"
    >
      <Flex justifyContent="space-between" alignItems="flex-start">
        <Box>
          <H2>Divisions & Teams</H2>
          <Paragraph color="gray.60" mt={0}>
            The Divisions and Teams your account is organised into. The
            Default Division and Team are created automatically and can't be
            removed — every record not otherwise assigned belongs to them.
          </Paragraph>
        </Box>

        <Flex position="relative" ref={newMenuRef}>
          <Button
            Icon={FaPlus}
            iconSize="16px"
            onClick={() => setIsNewMenuOpen((v) => !v)}
          >
            New
          </Button>

          {isNewMenuOpen && (
            <Box
              style={{
                position: "absolute",
                top: "100%",
                right: 0,
                background: "white",
                boxShadow: "0px 4px 6px rgba(0,0,0,0.1)",
                borderRadius: "12px",
                zIndex: 10,
                minWidth: "160px",
              }}
            >
              <Paragraph
                my={0}
                style={{
                  padding: "10px",
                  cursor: hasPermission("divisions.manage")
                    ? "pointer"
                    : "not-allowed",
                  opacity: hasPermission("divisions.manage") ? 1 : 0.4,
                }}
                onClick={
                  hasPermission("divisions.manage") ? openNewDivision : undefined
                }
                title={
                  hasPermission("divisions.manage")
                    ? undefined
                    : "You don't have permission to manage divisions"
                }
              >
                Division
              </Paragraph>
              <Paragraph
                my={0}
                style={{
                  padding: "10px",
                  cursor: hasPermission("teams.manage")
                    ? "pointer"
                    : "not-allowed",
                  opacity: hasPermission("teams.manage") ? 1 : 0.4,
                }}
                onClick={hasPermission("teams.manage") ? openNewTeam : undefined}
                title={
                  hasPermission("teams.manage")
                    ? undefined
                    : "You don't have permission to manage teams"
                }
              >
                Team
              </Paragraph>
            </Box>
          )}
        </Flex>
      </Flex>

      {isLoading && (
        <Flex justifyContent="center" p={4}>
          <Loader />
        </Flex>
      )}

      {!isLoading && !divisions?.data?.length && (
        <Paragraph textAlign="center">No divisions found.</Paragraph>
      )}

      {!isLoading && divisions?.data?.length > 0 && (
        <>
          <Flex justifyContent="flex-end" gap="24" pr={2} mb={1}>
            <Paragraph color="gray.50" m="0px" minWidth="24px" textAlign="right">
              Users
            </Paragraph>
            <Box width="20px" />
            <Box width="20px" />
          </Flex>

          <Box>
            {divisions?.data?.map((division) => (
              <ExpansionPanel
                key={division.id}
                iconPosition="start"
                headerPy={2}
                showHeaderDivider
                contentMt={0}
                defaultExpanded
                title={
                  <Flex
                    justifyContent="space-between"
                    alignItems="center"
                    width="100%"
                  >
                    <Flex alignItems="center" gap="8">
                      <Paragraph fontWeight="bold" m="0px">
                        {division.display_name}
                      </Paragraph>
                      {division.is_default && (
                        <DefaultPill>Default</DefaultPill>
                      )}
                    </Flex>
                    <Flex alignItems="center" gap="24">
                      <Paragraph
                        m="0px"
                        color="blue.primary"
                        minWidth="24px"
                        textAlign="right"
                      >
                        {division.user_count}
                      </Paragraph>
                      {division.can_delete && !division.is_default ? (
                        <Image
                          src={deleteIcon}
                          alt="deactivate division"
                          height="20px"
                          width="20px"
                          style={{
                            cursor: hasPermission("divisions.manage")
                              ? "pointer"
                              : "not-allowed",
                            opacity: hasPermission("divisions.manage") ? 1 : 0.4,
                          }}
                          title={
                            hasPermission("divisions.manage")
                              ? "Deactivate division"
                              : "You don't have permission to manage divisions"
                          }
                          onClick={(e: any) => {
                            if (!hasPermission("divisions.manage")) return;
                            e.stopPropagation();
                            setPendingDeletion({ kind: "division", item: division });
                          }}
                        />
                      ) : (
                        <Box width="20px" />
                      )}
                      <Image
                        src={editIcon}
                        alt="edit division"
                        height="20px"
                        width="20px"
                        style={{
                          cursor: hasPermission("divisions.manage")
                            ? "pointer"
                            : "not-allowed",
                          opacity: hasPermission("divisions.manage") ? 1 : 0.4,
                        }}
                        title={
                          hasPermission("divisions.manage")
                            ? undefined
                            : "You don't have permission to manage divisions"
                        }
                        onClick={(e: any) => {
                          if (!hasPermission("divisions.manage")) return;
                          e.stopPropagation();
                          openEditDivision(division);
                        }}
                      />
                    </Flex>
                  </Flex>
                }
              >
                {teamsByDivision(division.id).map((team) => (
                  <Flex
                    key={team.id}
                    justifyContent="space-between"
                    alignItems="center"
                    py={2}
                    borderBottom="1px solid"
                    borderBottomColor="gray.10"
                  >
                    <Flex alignItems="center" gap="12">
                      <Box width="26px" />
                      <Flex alignItems="center" gap="8">
                        <Paragraph m="0px" color="gray.60">
                          {team.display_name}
                        </Paragraph>
                        {team.is_default && <DefaultPill>Default</DefaultPill>}
                      </Flex>
                    </Flex>
                    <Flex alignItems="center" gap="24">
                      <Paragraph
                        m="0px"
                        color="blue.primary"
                        minWidth="24px"
                        textAlign="right"
                      >
                        {team.user_count}
                      </Paragraph>
                      {team.can_delete && !team.is_default ? (
                        <Image
                          src={deleteIcon}
                          alt="delete team"
                          height="20px"
                          width="20px"
                          style={{
                            cursor: teamDeleteBlocked(team)
                              ? "not-allowed"
                              : "pointer",
                            opacity: teamDeleteBlocked(team) ? 0.4 : 1,
                          }}
                          title={teamDeleteBlocked(team)}
                          onClick={() =>
                            !teamDeleteBlocked(team) &&
                            setPendingDeletion({ kind: "team", item: team })
                          }
                        />
                      ) : (
                        <Box width="20px" />
                      )}
                      <Image
                        src={editIcon}
                        alt="edit team"
                        height="20px"
                        width="20px"
                        style={{
                          cursor: hasPermission("teams.manage")
                            ? "pointer"
                            : "not-allowed",
                          opacity: hasPermission("teams.manage") ? 1 : 0.4,
                        }}
                        title={
                          hasPermission("teams.manage")
                            ? undefined
                            : "You don't have permission to manage teams"
                        }
                        onClick={() =>
                          hasPermission("teams.manage") && openEditTeam(team)
                        }
                      />
                    </Flex>
                  </Flex>
                ))}
                {teamsByDivision(division.id).length === 0 && (
                  <Flex alignItems="center" gap="12" py={2}>
                    <Box width="26px" />
                    <Paragraph color="gray.50" m="0px">
                      No teams in this division.
                    </Paragraph>
                  </Flex>
                )}
              </ExpansionPanel>
            ))}
          </Box>
        </>
      )}

      <AddEditDivisionModal
        isOpen={isDivisionModalOpen}
        onRequestClose={() => setIsDivisionModalOpen(false)}
        division={editingDivision}
      />
      <AddEditTeamModal
        isOpen={isTeamModalOpen}
        onRequestClose={() => setIsTeamModalOpen(false)}
        team={editingTeam}
      />

      <ConfirmModal
        isOpen={Boolean(pendingDeletion)}
        onRequestClose={() => setPendingDeletion(null)}
        handleConfirm={confirmPendingDeletion}
        title={
          pendingDeletion?.kind === "division"
            ? `Deactivate "${pendingDeletion.item.display_name}"?`
            : pendingDeletion?.kind === "team"
            ? `Delete "${pendingDeletion.item.display_name}"?`
            : undefined
        }
        content={
          pendingDeletion?.kind === "division"
            ? "This Division will be deactivated and hidden from new records. You can reactivate it later by editing it and turning Active back on."
            : pendingDeletion?.kind === "team"
            ? "This permanently removes the Team and can't be undone."
            : undefined
        }
      />
    </Box>
  );
};

export default SettingsDivisionsTeams;
